// src/utils/smartPlanManager.js
import { SmartDateParser } from "./dateParser";
import { DayPlanService } from "../services/dayPlanService";

export class SmartPlanManager {
  constructor() {
    this.dateParser = new SmartDateParser();
    this.dayPlanService = new DayPlanService();

    // Statistics
    this.stats = {
      totalRequests: 0,
      successfulRequests: 0,
      averageConfidence: 0,
      lastRequestTime: null,
    };

    console.log("🎯 SmartPlanManager initialized");
  }

  async processUserRequest(userInput, context = {}) {
    const startTime = Date.now();
    this.stats.totalRequests++;
    this.stats.lastRequestTime = new Date().toISOString();

    try {
      console.log("🔍 Processing user request:", userInput);
      console.log("📋 Context:", context);

      // 1. Parse user input
      const parseResult = this.dateParser.parseUserInput(userInput);
      console.log("📊 Parse result:", parseResult);

      // 2. Validate parse result
      const validation = this.validateParseResult(parseResult);
      if (!validation.isValid) {
        return this.createFailureResponse(
          validation.reason,
          validation.suggestions,
          parseResult
        );
      }

      // 3. Check abort signal
      if (context.abortSignal?.aborted) {
        throw new Error("Request was aborted");
      }

      // 4. Fetch activity data
      let planResult = await this.fetchActivitiesForDays(
        parseResult,
        context
      );
      console.log("54",planResult)



      // 6. Transform data format
      const itinerary = this.dayPlanService.transformToItineraryFormat(
        planResult,
        parseResult.startDate,
        parseResult.endDate
      );

      // 7. Generate success response
      const successResponse = this.createSuccessResponse(
        parseResult,
        planResult,
        itinerary
      );

      // 8. Update statistics
      this.stats.successfulRequests++;
      this.updateAverageConfidence(parseResult.confidence);

      const processingTime = Date.now() - startTime;
      console.log(`⚡ Processing completed in ${processingTime}ms`);

      return successResponse;
    } catch (error) {
      console.error("❌ Error processing user request:", error);

      if (error.message.includes("aborted")) {
        return {
          success: false,
          error: "CANCELLED",
          message: "Request was cancelled",
          timestamp: Date.now(),
        };
      }

      return {
        success: false,
        error: "PROCESSING_ERROR",
        message: "An error occurred while processing your request. Please try again.",
        details: error.message,
        suggestions: ["Check your internet connection", "Simplify your request", "Try again later"],
        timestamp: Date.now(),
      };
    }
  }

  validateParseResult(parseResult) {
    const { days, confidence, startDate, endDate } = parseResult;

    console.log("🔍 Validating parse result:", parseResult);

    // 🔥 Lower confidence threshold from 0.3 to 0.15
    if (confidence < 0.15) {
      console.log("❌ Confidence too low:", confidence);
      return {
        isValid: false,
        reason: "LOW_CONFIDENCE",
        suggestions: [
          'Please specify the number of days more clearly, e.g., "5-day itinerary"',
          'Specify the exact dates, e.g., "May 1–5"',
          'Use a standard format, e.g., "3 days, 2 nights"',
        ],
      };
    }

    // Days validity check
    if (!days || days < 1 || days > 30) {
      console.log("❌ Invalid days:", days);
      return {
        isValid: false,
        reason: "INVALID_DAYS",
        suggestions: [
          "Please specify a trip length between 1 and 30 days.",
          'Try saying "5-day Montreal itinerary"',
          'Or "one-week tour"',
        ],
      };
    }

    // Date validity check (keep original logic)
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const today = new Date();

      if (start > end) {
        console.log("❌ Invalid date range:", startDate, "to", endDate);
        return {
          isValid: false,
          reason: "INVALID_DATE_RANGE",
          suggestions: [
            "Please check your start and end dates",
            'Try saying "5-day Montreal itinerary"',
            'Or specify correct date range',
          ],
        };
      }

      // Check if date is too far in the past
      const oneWeekAgo = new Date(today);
      oneWeekAgo.setDate(today.getDate() - 7);

      if (end < oneWeekAgo) {
        console.log("❌ Date too old:", endDate);
        return {
          isValid: false,
          reason: "DATE_TOO_OLD",
          suggestions: [
            "Please select a future date",
            'Or use relative time such as "next week"',
            "Specify an exact future date",
          ],
        };
      }
    }

    console.log("✅ Parse result validation passed");
    return { isValid: true };
  }

  async fetchActivitiesForDays(parseResult, context) {
    const { days } = parseResult;
    console.log("179 context",context)
    const preferences = context.travelPreferences


    if (!days || days <= 0) {
      throw new Error("Invalid number of days");
    }

    console.log(`🗓️ Fetching activities for ${days} days`);

    try {
      // Check abort signal
      if (context.abortSignal?.aborted) {
        throw new Error("Request was aborted");
      }
      console.log("210",preferences)

      // Call API to get multi-day plan
      const multiDayPlan = await this.dayPlanService.fetchDayPlan(days,preferences);

      // Check coverage
      if (multiDayPlan.coverage < 0.5) {
        console.warn(`⚠️ Low coverage: ${multiDayPlan.coverage * 100}%`);

        // If coverage is too low, generate fallback plan
        const fallbackPlan = await this.dayPlanService.generateFallbackPlan(
          days,
          preferences
        );
        return {
          ...fallbackPlan,
          isPartialData: true,
          originalCoverage: multiDayPlan.coverage,
        };
      }

      return multiDayPlan;
    } catch (error) {
      console.error("❌ Error fetching activities:", error);

      // Try to generate fallback plan
      try {
        console.log("🔄 Generating fallback plan due to API error...");
        const fallbackPlan = await this.dayPlanService.generateFallbackPlan(
          days,
          preferences
        );
        return {
          ...fallbackPlan,
          isFallback: true,
          originalError: error.message,
        };
      } catch (fallbackError) {
        console.error("❌ Fallback plan also failed:", fallbackError);
        throw error; // Re-throw original error
      }
    }
  }

  createSuccessResponse(parseResult, planResult, itinerary) {
    const { days, startDate, endDate, intent, preferences } = parseResult;

    let message = `✨ Perfect! I've planned a ${days}-day Montreal itinerary for you`;

    if (startDate && endDate) {
      message += ` (${startDate} to ${endDate})`;
    }

    message += `.\n\n📋 **Itinerary Overview:**\n`;
    message += `• Total Days: ${days} days\n`;

    if (startDate && endDate) {
      message += `• Start Date: ${startDate}\n`;
      message += `• End Date: ${endDate}\n`;
    }

    message += `• Activities Planned: ${planResult.totalReceived}/${planResult.totalRequested} days\n`;

    if (planResult.isFallback) {
      message += `• Note: Using curated recommendation templates\n`;
    }

    if (preferences && Object.keys(preferences).length > 0) {
      message += `• Preferences: `;
      const prefList = [];
      if (preferences.activity) prefList.push(preferences.activity);
      if (preferences.budget) prefList.push(preferences.budget);
      if (preferences.tripType) prefList.push(preferences.tripType);
      message += prefList.join(", ") + "\n";
    }

    message += `\n🎯 This itinerary includes Montreal's most exciting attractions and experiences. You can review each day's detailed schedule and make adjustments anytime!`;

    return {
      success: true,
      parseResult,
      planResult,
      itinerary,
      message,
      metadata: {
        confidence: parseResult.confidence,
        daysPlanned: days,
        dateRange: startDate && endDate ? `${startDate} to ${endDate}` : null,
        preferences: preferences || {},
        intent: intent,
        isPartialData: planResult.isPartialData || false,
        isFallback: planResult.isFallback || false,
        coverage: planResult.coverage || 1.0,
        processingTime: Date.now(),
        apiCalls: planResult.totalRequested || 0,
      },
      actions: [
        {
          type: "view_itinerary",
          label: "View Detailed Itinerary",
          data: itinerary,
        },
        {
          type: "modify_itinerary",
          label: "Modify Itinerary",
          available: true,
        },
        {
          type: "export_pdf",
          label: "Export PDF",
          available: true,
        },
      ],
    };
  }

  createFailureResponse(reason, suggestions = [], parseResult = null) {
    const errorMessages = {
      LOW_CONFIDENCE:
        'I couldn\'t understand the duration or dates you want. Please be more specific, like "5-day trip" or "May 1st to 4th".',
      INVALID_DAYS: "Please specify a reasonable duration (1-30 days).",
      INVALID_DATE_RANGE:
        "There's an issue with the date range you provided, please check and re-enter.",
      DATE_TOO_OLD:
        "The dates you specified are in the past, please choose future dates.",
      PLAN_GENERATION_FAILED:
        "Unable to generate the requested itinerary at the moment, please try other options.",
      INSUFFICIENT_INFO: "I need more information to plan your trip.",
    };

    return {
      success: false,
      error: reason,
      message: errorMessages[reason] || "There was a problem processing your request.",
      suggestions:
        suggestions.length > 0
          ? suggestions
          : [
              "Plan a 5-day Montreal trip",
              "May 1st to May 5th travel plan",
              "Arrange a 3-day 2-night itinerary",
            ],
      parseResult,
      metadata: {
        confidence: parseResult?.confidence || 0,
        timestamp: Date.now(),
        failureReason: reason,
      },
    };
  }

  updateAverageConfidence(newConfidence) {
    const currentAvg = this.stats.averageConfidence;
    const total = this.stats.successfulRequests;

    this.stats.averageConfidence =
      (currentAvg * (total - 1) + newConfidence) / total;
  }

  // Check if user input contains date/day information
  static containsDateInfo(input) {
    if (!input || typeof input !== "string") {
      return false;
    }

    const lowerInput = input.toLowerCase().trim();

    console.log("🔍 Checking date info for:", lowerInput);

    // 1. First check explicit date patterns
    const strongDatePatterns = [
      /\b\d+\s*(?:day|days|night|nights)\b/i, // 5 days, 3 days, 2 nights
      /\b\d{1,2}[\/\-]\d{1,2}\b/, // 5/1, 5-1
      /\b\d{4}[-\/]\d{1,2}[-\/]\d{1,2}\b/, // 2025-05-01
      /\b(?:january|february|march|april|may|june|july|august|september|october|november|december)\s+\d{1,2}\b/i, // May 15
      /\b(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\s+\d{1,2}\b/i, // May 15
      /\b\d{1,2}\s*(?:st|nd|rd|th)\s+(?:january|february|march|april|may|june|july|august|september|october|november|december)\b/i, // 15th May
      /\b(?:next|this)\s+(?:week|month|weekend)\b/i, // next week, this month
      /\b(?:tomorrow|today)\b/i, // tomorrow, today
    ];

    // Check strong date patterns
    const hasStrongDatePattern = strongDatePatterns.some((pattern) =>
      pattern.test(lowerInput)
    );

    if (hasStrongDatePattern) {
      console.log("✅ Strong date pattern detected");
      return true;
    }

    // 2. Check number + specific keyword combinations
    const hasNumbers = /\b\d+\b/.test(lowerInput);

    if (hasNumbers) {
      // Only check these specific planning keywords when there are numbers
      const planningKeywords = [
        /\b(?:day|days)\s+(?:in|at|to|for)\b/i, // "5 days in Montreal"
        /\b(?:day|days)\s+(?:trip|tour|itinerary|plan)\b/i, // "5 day trip"
        /\b(?:week|weeks)\s+(?:in|at|to|for|trip|tour)\b/i, // "2 week tour"
        /\bitinerary\b/i, // itinerary (strong planning word)
        /\bschedule\b.*\b(?:day|days|visit)\b/i, // schedule with days/visit
      ];

      const hasStrongPlanningPattern = planningKeywords.some((pattern) =>
        pattern.test(lowerInput)
      );

      if (hasStrongPlanningPattern) {
        console.log("✅ Number + planning pattern detected");
        return true;
      }
    }

    // 3. Check explicit Chinese date expressions (keep for potential Chinese users)
    const chineseDatePatterns = [
      /[一二三四五六七八九十\d]+[天日]/,
      /[一二三四五六七八九十\d]+晚/,
      /行程|计划.*[天日]/,
      /[下本这]周/,
      /周末/,
      /明天|后天/,
    ];

    const hasChineseDatePattern = chineseDatePatterns.some((pattern) =>
      pattern.test(lowerInput)
    );

    if (hasChineseDatePattern) {
      console.log("✅ Chinese date pattern detected");
      return true;
    }

    console.log("❌ No date info detected");
    return false;
  }

  // Get statistics
  getStats() {
    return {
      ...this.stats,
      successRate:
        this.stats.totalRequests > 0
          ? (
              (this.stats.successfulRequests / this.stats.totalRequests) *
              100
            ).toFixed(2) + "%"
          : "0%",
    };
  }

  // Reset statistics
  resetStats() {
    this.stats = {
      totalRequests: 0,
      successfulRequests: 0,
      averageConfidence: 0,
      lastRequestTime: null,
    };
  }
}